import React, { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";

dayjs.extend(customParseFormat);

// DD/MM/YYYY from whatever digits were typed: "18112001" -> "18/11/2001"; slashes are added as you type.
const mask = (raw) => {
  const d = String(raw).replace(/\D/g, "").slice(0, 8);
  if (d.length <= 2) return d;
  if (d.length <= 4) return `${d.slice(0, 2)}/${d.slice(2)}`;
  return `${d.slice(0, 2)}/${d.slice(2, 4)}/${d.slice(4)}`;
};

/**
 * Date-of-birth box that types predictably: digits only, slashes auto-added, a calendar button for picking.
 * value: dayjs | null. onChange gets a valid dayjs once all 8 digits make a real date,
 * null when emptied, and an invalid dayjs while half-typed or impossible (e.g. 31/02/2000).
 */
export default function DobInput({ value, onChange, label = "Dob", error, helperText, minDate, maxDate }) {
  const [text, setText] = useState(value && value.isValid && value.isValid() ? value.format("DD/MM/YYYY") : "");
  const [open, setOpen] = useState(false);
  const anchor = useRef(null);

  // Follow outside changes (member loaded, date picked in the calendar, form reset).
  useEffect(() => {
    if (!value) {
      if (text.length === 10) setText("");
      return;
    }
    if (value.isValid && value.isValid()) {
      const f = value.format("DD/MM/YYYY");
      if (f !== text) setText(f);
    }
  }, [value]);

  const handleType = (e) => {
    const t = mask(e.target.value);
    setText(t);
    if (!t) return onChange(null);
    onChange(t.length === 10 ? dayjs(t, "DD/MM/YYYY", true) : dayjs(NaN));
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <TextField
        ref={anchor}
        fullWidth
        label={label}
        value={text}
        onChange={handleType}
        placeholder="DD/MM/YYYY"
        inputProps={{ inputMode: "numeric", maxLength: 10, autoComplete: "off" }}
        error={error}
        helperText={helperText}
        InputProps={{
          endAdornment: (
            <InputAdornment position="end">
              <IconButton edge="end" aria-label="Pick date" onClick={() => setOpen(true)}>
                <CalendarMonthIcon />
              </IconButton>
            </InputAdornment>
          ),
        }}
      />
      <DatePicker
        open={open}
        onClose={() => setOpen(false)}
        value={value && value.isValid && value.isValid() ? value : null}
        onChange={(d) => d && onChange(d)}
        onAccept={() => setOpen(false)}
        minDate={minDate}
        maxDate={maxDate}
        openTo="year"
        views={["year", "month", "day"]}
        slots={{ field: () => null }}
        slotProps={{ popper: { anchorEl: () => anchor.current } }}
      />
    </LocalizationProvider>
  );
}
